import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { Header } from './Dashboard';

const UNIT = { hour: '/hr', day: '/day', event: '/event' };

export default function Companions() {
  const [companions, setCompanions] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [viewId, setViewId] = useState(null);

  const load = useCallback(async () => {
    const [c, cfg] = await Promise.all([api.get('/admin/companions'), api.get('/admin/config')]);
    setCompanions(c.companions);
    setEnabled(cfg.config.companionEnabled);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleFeature = async () => {
    const next = !enabled;
    setEnabled(next);
    await api.patch('/admin/config', { companionEnabled: next });
  };

  const update = async (id, patch) => {
    await api.patch(`/admin/companions/${id}`, patch);
    load();
  };

  return (
    <div>
      <Header title="Friend on Rent" sub="Manage the companionship feature and its listings" />

      <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Feature {enabled ? 'enabled' : 'disabled'}</div>
          <div style={{ color: 'var(--text-dim)', fontSize: 13, marginTop: 4, maxWidth: 560 }}>
            When off, the section is hidden in the app and no new requests can be sent. Turn off for App/Play Store builds if needed.
          </div>
        </div>
        <button className={`btn ${enabled ? 'danger' : 'green'}`} onClick={toggleFeature}>
          {enabled ? 'Disable feature' : 'Enable feature'}
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table>
          <thead>
            <tr><th>User</th><th>Rate</th><th>Cities</th><th>Listed</th><th>Visible</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : companions.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>No one has listed yet.</td></tr>
            ) : companions.map((u) => (
              <tr key={u._id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img src={u.photos?.[0]?.url || 'https://via.placeholder.com/40'} alt="" style={{ width: 40, height: 40, borderRadius: 20, objectFit: 'cover' }} />
                    <div>
                      <div style={{ color: '#fff', fontWeight: 700 }}>{u.name || '—'} {u.isVerified ? '✔️' : ''}</div>
                      <div style={{ fontSize: 12, textTransform: 'capitalize' }}>{u.gender}</div>
                    </div>
                  </div>
                </td>
                <td style={{ color: 'var(--like)', fontWeight: 700 }}>₹{u.companion?.rate || 0}{UNIT[u.companion?.rateUnit] || '/hr'}</td>
                <td>{(u.companion?.cities || []).join(', ') || '—'}</td>
                <td><span className={`badge ${u.companion?.enabled ? 'active' : 'incomplete'}`}>{u.companion?.enabled ? 'Listed' : 'Off'}</span></td>
                <td><span className={`badge ${u.companion?.approved ? 'active' : 'blocked'}`}>{u.companion?.approved ? 'Visible' : 'Hidden'}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn sm" style={{ background: 'var(--secondary)' }} onClick={() => setViewId(u._id)}>👁 View</button>
                    {u.companion?.approved ? (
                      <button className="btn sm danger" onClick={() => update(u._id, { approved: false })}>Hide</button>
                    ) : (
                      <button className="btn sm green" onClick={() => update(u._id, { approved: true })}>Show</button>
                    )}
                    <button className="btn sm muted" onClick={() => update(u._id, { enabled: false })}>Remove listing</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewId ? <CompanionModal id={viewId} onClose={() => setViewId(null)} /> : null}
    </div>
  );
}

function CompanionModal({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/companions/${id}`).then(setData); }, [id]);

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div className="card" style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {!data ? <div>Loading…</div> : (
          <>
            <div style={styles.modalHead}>
              <strong style={{ fontSize: 18 }}>{data.user.name || '—'} {data.user.isVerified ? '✔️' : ''}</strong>
              <button className="btn ghost sm" onClick={onClose}>✕</button>
            </div>

            {/* Photo gallery */}
            <div style={styles.gallery} className="scrollbar">
              {(data.user.photos || []).map((p) => <img key={p._id || p.url} src={p.url} alt="" style={styles.galleryImg} />)}
              {(data.user.photos || []).length === 0 ? <div style={{ color: 'var(--text-muted)' }}>No photos</div> : null}
            </div>

            {/* Details */}
            <div style={styles.grid}>
              <Detail label="Phone" value={`+${data.user.phone || '—'}`} />
              <Detail label="Gender" value={data.user.gender} />
              <Detail label="Age" value={data.user.age || '—'} />
              <Detail label="Rate" value={`₹${data.user.companion?.rate || 0}${UNIT[data.user.companion?.rateUnit] || '/hr'}`} />
              <Detail label="Cities" value={(data.user.companion?.cities || []).join(', ') || '—'} />
              <Detail label="Profession" value={data.user.profession || '—'} />
            </div>
            {data.user.companion?.tagline ? <Detail label="Note" value={data.user.companion.tagline} /> : null}
            {data.user.bio ? <Detail label="Bio" value={data.user.bio} /> : null}

            {/* Request log */}
            <h4 style={styles.h4}>Requests received ({data.requestsReceived.length})</h4>
            <div style={styles.log} className="scrollbar">
              {data.requestsReceived.length === 0 ? <div style={{ color: 'var(--text-muted)', padding: 8 }}>No one has applied yet.</div> : null}
              {data.requestsReceived.map((r, i) => (
                <div key={i} style={styles.logRow}>
                  <img src={r.user?.photos?.[0]?.url || 'https://via.placeholder.com/32'} alt="" style={styles.logAvatar} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#fff', fontWeight: 700 }}>{r.user?.name || '—'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>+{r.user?.phone}</span></div>
                    {r.message ? <div style={{ color: 'var(--text-dim)', fontSize: 13 }}>“{r.message}”</div> : null}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(r.createdAt).toLocaleDateString()}</div>
                </div>
              ))}
            </div>

            {data.requestsSent.length ? (
              <>
                <h4 style={styles.h4}>Requests sent ({data.requestsSent.length})</h4>
                <div style={styles.log} className="scrollbar">
                  {data.requestsSent.map((r, i) => (
                    <div key={i} style={styles.logRow}>
                      <img src={r.user?.photos?.[0]?.url || 'https://via.placeholder.com/32'} alt="" style={styles.logAvatar} />
                      <div style={{ flex: 1 }}><span style={{ color: '#fff', fontWeight: 700 }}>{r.user?.name || '—'}</span> <span style={{ color: 'var(--text-muted)' }}>+{r.user?.phone}</span></div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(r.createdAt).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{label}</div>
      <div style={{ color: 'var(--text)', fontSize: 14, textTransform: label === 'Gender' ? 'capitalize' : 'none' }}>{value}</div>
    </div>
  );
}

const styles = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20, padding: 20 },
  modal: { width: 620, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto' },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  gallery: { display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8, marginBottom: 14 },
  galleryImg: { width: 130, height: 170, borderRadius: 12, objectFit: 'cover', flex: '0 0 auto' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
  h4: { margin: '18px 0 10px', color: '#fff', fontSize: 14 },
  log: { background: 'var(--bg-alt)', borderRadius: 10, maxHeight: 220, overflowY: 'auto' },
  logRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: '1px solid var(--border)' },
  logAvatar: { width: 36, height: 36, borderRadius: 18, objectFit: 'cover' },
};
